// History (walkthroughs 7, 10) as rules: a session's state in words, its
// check-in and feedback read back, its plan set against what was done, and
// the way back to the list's page or the calendar's day it was opened from.
// Pure — pinned by history.test.js; the components only lay these out.
//
// calendar.js imports stateBadge from here and this file imports the
// calendar's URL rules from there. Neither calls the other while loading
// (only inside functions), so the two can import each other safely.
import { calendarSearch, historyView, parseDay, parseMonth } from "./calendar";
import { MUSCLES, REGIONS, formatDay, muscleLabel, regionLabel, templateLabel } from "./format";
import { sortSets } from "./sessionSets";

// backend/training/constants.py SESSION_PAGE_SIZE: sessions/ pages hold 20.
export const SESSION_PAGE_SIZE = 20;

// TrainingSessionStateEnum as History says it. A session idle for 6 hours
// is closed by the server (D18): "closed itself", in amber — it got no
// feedback, so its muscles held their weekly sets.
export const SESSION_STATES = {
  finished: { label: "Finished", variant: "secondary" },
  auto_closed: { label: "Closed itself", variant: "warning" },
  open: { label: "Open", variant: "default" },
};

const humanize = (code) => {
  const text = String(code ?? "").replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const has = (value) => value !== null && value !== undefined && value !== "";

/** The state's badge: `{label, variant}` (an unknown state still reads as words). */
export function stateBadge(state) {
  return SESSION_STATES[state] ?? { label: humanize(state), variant: "outline" };
}

/** "1 set", "8 sets", "0 sets". */
export const setsWord = (n) => `${n} ${Number(n) === 1 ? "set" : "sets"}`;

/** A session's name in History: "Upper · Wed, Oct 7" (the day in `timeZone`). */
export function sessionTitle(session, timeZone, now = new Date()) {
  const day = formatDay(session?.started_at, timeZone, now);
  const label = templateLabel(session?.template ?? "custom");
  return day ? `${label} · ${day}` : label;
}

/** "Sessions 21–40 of 45" — which rows this page shows. */
export function pageRange(page, count, pageSize = SESSION_PAGE_SIZE) {
  if (!(count > 0)) return "";
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(count, page * pageSize);
  return first === last ? `${first} of ${count}` : `${first}–${last} of ${count}`;
}

/** A session id from the URL: digits only (anything else is no session). */
export const isSessionId = (id) => typeof id === "string" && /^\d{1,12}$/.test(id);

/**
 * Where "back to History" goes, from the query the session was opened
 * from (carried in the link's state):
 * - the list's page (`?page=3`), kept as it is;
 * - the calendar's month and chosen day
 *   (`?view=calendar&month=2026-10&day=2026-10-06`), rebuilt from the parts
 *   that check out, as History itself reads them: a month that isn't real
 *   leaves this month's calendar, and a day outside the month is dropped;
 * - anything else: the list's first page.
 */
export function historyPath(search) {
  if (typeof search !== "string") return "/training/history";
  if (/^\?page=\d{1,6}$/.test(search)) return `/training/history${search}`;
  const params = search.startsWith("?") ? new URLSearchParams(search) : null;
  if (historyView(params?.get("view")) !== "calendar") return "/training/history";
  const month = parseMonth(params.get("month"));
  if (!month) return "/training/history?view=calendar";
  const day = parseDay(params.get("day"), month);
  return `/training/history?${calendarSearch({ month, day })}`;
}

// A main lift's key as Progress's `?table=` carries it ("bench-press").
const LIFT_KEY_RE = /^[a-z0-9_-]{1,64}$/;

/** The lifts whose tables are open, from Progress's query: `?table=` each, in order. */
export function openTables(search) {
  const params = typeof search === "string" && search.startsWith("?") ? new URLSearchParams(search) : null;
  return (params?.getAll("table") ?? []).filter((key) => LIFT_KEY_RE.test(key));
}

/**
 * Where "back to Progress" goes, from the query the session was opened from
 * (carried in the link's state): Progress with the tables it had open — the
 * keyboard path through its sessions — and nothing else.
 */
export function progressPath(search) {
  const tables = openTables(search);
  if (tables.length === 0) return "/training/progress";
  const params = new URLSearchParams();
  for (const key of tables) params.append("table", key);
  return `/training/progress?${params}`;
}

/**
 * A session page's way back, from its link's state: to Progress when it was
 * opened there (`{from: "progress", progressSearch}`), else to History
 * (`{historySearch}`, see historyPath). `{to, label}`.
 */
export function backTo(state) {
  if (state?.from === "progress") return { to: progressPath(state.progressSearch), label: "Back to Progress" };
  return { to: historyPath(state?.historySearch), label: "Back to History" };
}

// --- the check-in, read back ---------------------------------------------------

// backend/training/serializers.py TrainingCheckinSerializer's scales.
const FEELINGS = [
  { key: "sleep", label: "Sleep", scale: "1 poor … 5 great" },
  { key: "energy", label: "Energy", scale: "1 poor … 5 great" },
  { key: "motivation", label: "Motivation", scale: "1 poor … 5 great" },
  { key: "stress", label: "Stress", scale: "1 calm … 5 stressed" },
];
const JOINT_WORDS = {
  shoulder: "shoulder",
  elbow: "elbow",
  wrist: "wrist",
  knee: "knee",
  hip: "hip",
  low_back: "low back",
};
const REGION_ORDER = new Map(REGIONS.map((r, i) => [r.code, i]));

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/** One joint-pain row in words: "Left shoulder", "Knee, both sides", "Low back". */
export function jointWords({ joint, side } = {}) {
  const name = JOINT_WORDS[joint] ?? String(joint ?? "").replace(/_/g, " ");
  if (joint === "low_back") return capitalize(name);
  if (side === "left" || side === "right") return capitalize(`${side} ${name}`);
  return capitalize(`${name}, both sides`);
}

/**
 * The check-in as rows to read: the four scales ("4 of 5", with the
 * scale's ends), soreness per region, and joint pain. An unanswered
 * question has `value: null` (said as "not answered" — D12 holds on it).
 * Joint pain: null or missing → not answered; [] → "None".
 *
 * @returns {{key: string, label: string, value: string|null, scale?: string}[]}
 */
export function checkinFacts(checkin) {
  const rows = FEELINGS.map(({ key, label, scale }) => ({
    key,
    label,
    value: has(checkin?.[key]) ? `${checkin[key]} of 5` : null,
    scale,
  }));
  const soreness = Object.entries(checkin?.soreness ?? {})
    .filter(([, level]) => has(level))
    .sort(([a], [b]) => (REGION_ORDER.get(a) ?? 99) - (REGION_ORDER.get(b) ?? 99))
    .map(([region, level]) => `${regionLabel(region)} ${String(level).replace(/_/g, " ")}`);
  rows.push({ key: "soreness", label: "Soreness", value: soreness.length ? soreness.join(", ") : null });
  const pain = checkin?.joint_pain;
  rows.push({
    key: "joint_pain",
    label: "Joint pain",
    value: Array.isArray(pain) ? (pain.length ? pain.map(jointWords).join(", ") : "None") : null,
  });
  return rows;
}

// --- feedback, read back -------------------------------------------------------

// TrainingPumpEnum / TrainingWorkloadEnum, as the finish form says them.
const PUMP_WORDS = { low: "low", moderate: "moderate", amazing: "amazing" };
const WORKLOAD_WORDS = {
  easy: "easy",
  just_right: "just right",
  pushed_limits: "pushed my limits",
  too_much: "too much",
};
const MUSCLE_ORDER = new Map(MUSCLES.map((m, i) => [m.code, i]));

/**
 * The session's feedback as rows: `[{key, label, text}]` — "all" first
 * ("Every muscle"), then muscles in the catalog's order; text like
 * "pump amazing, workload pushed my limits". Empty answers are left out.
 */
export function feedbackRows(feedback) {
  return Object.entries(feedback ?? {})
    .map(([key, answer]) => {
      const parts = [];
      if (answer?.pump) parts.push(`pump ${PUMP_WORDS[answer.pump] ?? humanize(answer.pump).toLowerCase()}`);
      if (answer?.workload) {
        parts.push(`workload ${WORKLOAD_WORDS[answer.workload] ?? humanize(answer.workload).toLowerCase()}`);
      }
      return { key, label: key === "all" ? "Every muscle" : muscleLabel(key), text: parts.join(", ") };
    })
    .filter((row) => row.text)
    .sort((a, b) => {
      const rank = (row) => (row.key === "all" ? -1 : (MUSCLE_ORDER.get(row.key) ?? 99));
      return rank(a) - rank(b);
    });
}

// --- the plan against what was done ---------------------------------------------

/**
 * The session's exercises, grouped: each planned item in plan order with
 * the sets logged for it (oldest first), then each exercise logged off the
 * plan, in the order it was first done. A key the plan lists twice gets its
 * sets on its first item. `[{id, key, item: object|null, sets}]`.
 */
export function exerciseGroups(session) {
  const sets = sortSets(session?.sets);
  const byExercise = new Map();
  for (const set of sets) {
    if (!byExercise.has(set.exercise)) byExercise.set(set.exercise, []);
    byExercise.get(set.exercise).push(set);
  }
  const groups = [];
  const claimed = new Set();
  (session?.plan?.items ?? []).forEach((item, index) => {
    const first = !claimed.has(item.exercise);
    claimed.add(item.exercise);
    groups.push({
      id: `plan-${index}`,
      key: item.exercise,
      item,
      sets: first ? (byExercise.get(item.exercise) ?? []) : [],
    });
  });
  for (const [key, list] of byExercise) {
    if (!claimed.has(key)) groups.push({ id: `extra-${key}`, key, item: null, sets: list });
  }
  return groups;
}

/** "2 of 3 sets done", "4 sets done (3 planned)", "None done (3 planned)"; off the plan: "2 sets done". */
export function doneLine(item, done) {
  if (!item) return `${setsWord(done)} done`;
  const planned = item.sets ?? 0;
  if (done === 0) return `None done (${planned} planned)`;
  if (done > planned) return `${setsWord(done)} done (${planned} planned)`;
  return `${done} of ${setsWord(planned)} done`;
}

/** An exercise the catalog doesn't list (archived, or removed): enough to show and edit its sets. */
export const stubExercise = (key, name) => ({
  key,
  name: name ?? key,
  load_kind: "external",
  requires: [],
  joints: [],
});
